import moment from 'moment';
import { RECORD_TYPE } from './enums';
import dayjs from 'dayjs';
import { round } from 'lodash-es';

const processes = require('./processes');

export const processData = (reportData, settings) => {
  /**
   * Used to trim reportData records time to work time range
   * @returns {Object} reportData
   */
  const trimRecordsTime = () => {
    const { workStartTime, workEndTime, records } = reportData;

    const filteredRecords = records.filter((record) => {
      const utcOffsetClockInAt = moment(record.clockInAt);

      const utcOffsetClockOutAt = moment(record.clockOutAt);

      const clockInMinutes =
        parseInt(utcOffsetClockInAt.format('HH')) * 60 +
        parseInt(utcOffsetClockInAt.format('mm'));

      const clockInAndClockOutMinuteDiff = utcOffsetClockOutAt.diff(
        utcOffsetClockInAt,
        'minutes',
        true
      );

      const clockOutTotalMinutes =
        clockInMinutes + clockInAndClockOutMinuteDiff;

      const workStartTimeMinutes =
        parseInt(workStartTime.split(':')[0]) * 60 +
        parseInt(workStartTime.split(':')[1]);

      const workEndTimeMinutes =
        parseInt(workEndTime.split(':')[0]) * 60 +
        parseInt(workEndTime.split(':')[1]);

      // If clockIn is after workEndTime or clockOut is before workStartTime, do nothing and return
      if (
        clockInMinutes < workEndTimeMinutes &&
        clockOutTotalMinutes > workStartTimeMinutes
      )
        return record;
    });

    const croppedRecords = filteredRecords.map((record) => {
      const utcOffsetClockInAt = moment(record.clockInAt);

      const utcOffsetClockOutAt = moment(record.clockOutAt);

      const clockInMinutes =
        parseInt(utcOffsetClockInAt.format('HH')) * 60 +
        parseInt(utcOffsetClockInAt.format('mm'));
      const clockOutMinutes =
        parseInt(utcOffsetClockOutAt.format('HH')) * 60 +
        parseInt(utcOffsetClockOutAt.format('mm'));

      const workStartTimeMinutes =
        parseInt(workStartTime.split(':')[0]) * 60 +
        parseInt(workStartTime.split(':')[1]);

      const workEndTimeMinutes =
        parseInt(workEndTime.split(':')[0]) * 60 +
        parseInt(workEndTime.split(':')[1]);

      if (clockInMinutes < workStartTimeMinutes) {
        record.croppedClockInAt = moment(record.clockInAt)
          .startOf('day')
          .add(workStartTimeMinutes, 'minutes')
          .toISOString();
      } else {
        record.croppedClockInAt = record.clockInAt;
      }
      if (clockOutMinutes > workEndTimeMinutes) {
        record.croppedClockOutAt = moment(record.clockOutAt)
          .startOf('day')
          .add(workEndTimeMinutes, 'minutes')
          .toISOString();
      } else {
        record.croppedClockOutAt = record.clockOutAt;
      }
      return record;
    });

    reportData.records = croppedRecords;
    return reportData;
  };

  const initDays = () => {
    const fromDate = moment(reportData.fromDate).utcOffset(
      reportData.utcOffset,
      false
    );
    const toDate = moment(reportData.toDate).utcOffset(
      reportData.utcOffset,
      false
    );

    let days = {};
    let daysTypes = {};
    let i = fromDate;
    while (i.format('YYYY-MM-DD') <= toDate.format('YYYY-MM-DD')) {
      const day = i.format('YYYY-MM-DD');

      if (!(day in days)) {
        days[day] = {};
        daysTypes[day] = {};
        daysTypes[day][RECORD_TYPE.WORK] = {};
        daysTypes[day][RECORD_TYPE.PROJECTWORK] = {};
        daysTypes[day][RECORD_TYPE.ABSENCE] = {};
        daysTypes[day][RECORD_TYPE.ABSENCE_VACATION_PAID] = {};
        daysTypes[day][RECORD_TYPE.ABSENCE_SICK_LEAVE] = {};
        daysTypes[day][RECORD_TYPE.BREAK] = {};
        daysTypes[day][RECORD_TYPE.BREAK_COFFEE] = {};
        daysTypes[day][RECORD_TYPE.BREAK_MEAL] = {};
        daysTypes[day][RECORD_TYPE.SHIFT] = {};
        daysTypes[day][RECORD_TYPE.RIDE] = {};
        daysTypes[day][RECORD_TYPE.BENEFIT] = {};
        daysTypes[day][RECORD_TYPE.ITEM] = {};
        daysTypes[day][RECORD_TYPE.NEGATIVE] = {};
      }
      i.add(1, 'days');
    }
    reportData.days = days;
    reportData.daysTypes = daysTypes;

    return reportData;
  };

  const baseHours = () => {
    const records = reportData.records;
    let days = reportData.days;
    let daysTypes = reportData.daysTypes;

    const {
      recordAllowancesGroupedByRecordId,
      allowanceRecordsGroupedByParentRecordId,
      automaticMealBreakReductionLengthHours,
    } = reportData;

    const recordsWithExistingResource = records.filter(
      (record) => record.resource
    );

    let sortedRecords = [];

    try {
      recordsWithExistingResource.sort((a, b) =>
        a.croppedClockInAt.localeCompare(b.croppedClockInAt)
      );
      if (settings.lastNameFirst) {
        sortedRecords = recordsWithExistingResource.sort((a, b) =>
          a.resource.lastName.localeCompare(
            b.resource.lastName,
            settings.language,
            { sensitivity: 'base' }
          )
        );
      } else {
        sortedRecords = recordsWithExistingResource.sort((a, b) =>
          a.resource.firstName.localeCompare(
            b.resource.firstName,
            settings.language,
            { sensitivity: 'base' }
          )
        );
      }
    } catch (err) {
      console.log('ERROR 1');
    }

    let resources = {};

    sortedRecords.forEach((record) => {
      const recordProject = record.project;

      let clockOut = null;
      if (record.croppedClockOutAt) {
        clockOut = new Date(record.croppedClockOutAt);
      }

      if (clockOut) {
        let endDay = moment(record.croppedClockOutAt)
          .utc()
          .utcOffset(reportData.utcOffset, false);
        let i = moment(record.croppedClockInAt)
          .utc()
          .utcOffset(reportData.utcOffset, false);

        while (i.format('YYYY-MM-DD') <= endDay.format('YYYY-MM-DD')) {
          const day = i.format('YYYY-MM-DD');

          if (Object.keys(days).includes(day)) {
            if (!(day in days)) {
              days[day] = {};
            }
            const resource = record.resource;
            if (!(resource.user in days[day])) {
              days[day][resource.user] = {
                resource,
                hours: 0,
                reduction: 0,
                projectHours: {},
                reductions: {
                  [RECORD_TYPE.BREAK_COFFEE]: 0,
                  [RECORD_TYPE.BREAK_MEAL]: 0,
                  [RECORD_TYPE.BREAK]: 0,
                  AUTOMATIC_MEAL_BREAK_REDUCTION: 0,
                },
              };
            }
            if (daysTypes[day][record.type] !== undefined) {
              if (!(resource.user in daysTypes[day][record.type])) {
                daysTypes[day][record.type][resource.user] = {
                  value: 0,
                  reduction: 0,
                };
              }
            }
            if (!(resource.user in resources)) {
              resources[resource.user] = {
                resource,
                totalHours: 0,
                days: {},
                daysTypes: {},
                totals: {},
                reductions: {},
                automaticReductions: 0,
                seperateAdditionalSalaries: [],
                additionalSalaries: {},
                additionalSalariesByProjectId: {},
                workedProjects: [],
                dailyProjectsAtMealBreak: {},
              };
              resources[resource.user].totals[RECORD_TYPE.ABSENCE] = 0;
              resources[resource.user].totals[RECORD_TYPE.ABSENCE_SICK_LEAVE] =
                0;
              resources[resource.user].totals[
                RECORD_TYPE.ABSENCE_VACATION_PAID
              ] = 0;
              resources[resource.user].totals[RECORD_TYPE.BENEFIT] = 0;
              resources[resource.user].totals[RECORD_TYPE.BREAK] = 0;
              resources[resource.user].totals[RECORD_TYPE.BREAK_COFFEE] = 0;
              resources[resource.user].totals[RECORD_TYPE.BREAK_MEAL] = 0;
              resources[resource.user].totals[RECORD_TYPE.ITEM] = 0;
              resources[resource.user].totals[RECORD_TYPE.RIDE] = 0;
              resources[resource.user].totals[RECORD_TYPE.NEGATIVE] = 0;
              resources[resource.user].totals[RECORD_TYPE.SHIFT] = 0;
              resources[resource.user].totals[RECORD_TYPE.WORK] = 0;
              resources[resource.user].totals[RECORD_TYPE.PROJECTWORK] = 0;
              resources[resource.user].reductions[RECORD_TYPE.WORK] = 0;
              resources[resource.user].reductions[RECORD_TYPE.PROJECTWORK] = 0;
              resources[resource.user].totals.reductionsByProject = {};
            }
            if (!(day in resources[resource.user].days)) {
              resources[resource.user].days[day] = {
                hours: 0,
                reduction: 0,
              };
            }
            if (!(day in resources[resource.user].daysTypes)) {
              resources[resource.user].daysTypes[day] = {};
            }
            if (!(record.type in resources[resource.user].daysTypes[day])) {
              resources[resource.user].daysTypes[day][record.type] = {
                value: 0,
                reduction: 0,
              };
            }

            const co = moment.min(
              moment(record.croppedClockOutAt),
              moment(i).set({
                hours: 23,
                minute: 59,
                second: 59,
                millisecond: 999,
              })
            );
            const ci = moment.max(
              moment(record.croppedClockInAt),
              moment(i).set({
                hours: 0,
                minute: 0,
                second: 0,
                millisecond: 0,
              })
            );
            const hours = co.diff(ci, 'hours', true);

            daysTypes[day][record.type][resource.user].value += hours;

            // count break record reductions for each project
            if (record.type.includes('BREAK')) {
              const existingCountedReductionsForProject =
                resources[resource.user].totals.reductionsByProject[
                  recordProject.id
                ];

              if (!existingCountedReductionsForProject) {
                resources[resource.user].totals.reductionsByProject[
                  recordProject.id
                ] = {
                  BREAK_MEAL: 0,
                  BREAK_COFFEE: 0,
                  BREAK: 0,
                  AUTOMATIC_MEAL_BREAK_REDUCTION: 0,
                };
              }
              resources[resource.user].totals.reductionsByProject[
                recordProject.id
              ][record.type] += hours;

              if (!days[day][resource.user].projectHours[recordProject.id]) {
                days[day][resource.user].projectHours[recordProject.id] = {
                  hours: 0,
                  projectName: recordProject.name,
                  talosoftaProjectId: recordProject.talosoftaProjectId,
                  reductions: {
                    BREAK_MEAL: 0,
                    BREAK_COFFEE: 0,
                    BREAK: 0,
                    AUTOMATIC_MEAL_BREAK_REDUCTION: 0,
                  },
                };
              }
              days[day][resource.user].projectHours[
                recordProject.id
              ].reductions[record.type] += hours;
            }

            if (
              record.type === RECORD_TYPE.WORK ||
              record.type === RECORD_TYPE.PROJECTWORK
            ) {
              days[day][resource.user].hours += hours;

              const thisDayHoursExceed6Hours =
                days[day][resource.user].hours > 6;

              if (thisDayHoursExceed6Hours) {
                days[day][resource.user].reduction =
                  automaticMealBreakReductionLengthHours;
                days[day][resource.user].reductions[
                  'AUTOMATIC_MEAL_BREAK_REDUCTION'
                ] = automaticMealBreakReductionLengthHours;
                if (!daysTypes[day][record.type][resource.user].reduction) {
                  daysTypes[day][record.type][resource.user].reduction =
                    automaticMealBreakReductionLengthHours;
                  resources[resource.user].automaticReductions +=
                    automaticMealBreakReductionLengthHours;
                }
              }

              if (!days[day][resource.user].projectHours[recordProject.id]) {
                days[day][resource.user].projectHours[recordProject.id] = {
                  hours: 0,
                  projectName: recordProject.name,
                  talosoftaProjectId: recordProject.talosoftaProjectId,
                  reductions: {
                    BREAK_MEAL: 0,
                    BREAK_COFFEE: 0,
                    BREAK: 0,
                    AUTOMATIC_MEAL_BREAK_REDUCTION: 0,
                  },
                };
              }

              days[day][resource.user].projectHours[recordProject.id].hours +=
                hours;

              const recordAllowances = recordAllowancesGroupedByRecordId
                ? recordAllowancesGroupedByRecordId[record.id]
                : undefined;

              const allowanceRecords = allowanceRecordsGroupedByParentRecordId
                ? allowanceRecordsGroupedByParentRecordId[record.id]
                : undefined;

              if (recordAllowances && recordAllowances.length > 0) {
                recordAllowances.forEach((recordAllowance) => {
                  const { salaryType, projectId, unitCount } = recordAllowance;

                  const existingAddedAdditionalSalaryData =
                    resources[resource.user].additionalSalaries[salaryType];

                  const momentDate = moment(record.croppedClockInAt);

                  const date = momentDate.format('D.M.');

                  const fullDate = momentDate.format('DD.MM.YYYY');

                  const toolAllowanceUnitPrice =
                    resources[resource.user].resource?.toolAllowance;

                  const effectiveUnitPrice =
                    salaryType === 'TAXABLE_REIMBURSEMENT_OF_EXPENSES'
                      ? toolAllowanceUnitPrice
                      : '';

                  resources[resource.user].seperateAdditionalSalaries.push({
                    unitPrice: effectiveUnitPrice,
                    unitCount,
                    date: fullDate,
                    ISOStringDate: momentDate.toISOString(),
                    salaryType,
                    projectId,
                    talosoftaProjectId: recordProject.talosoftaProjectId,
                  });

                  if (existingAddedAdditionalSalaryData) {
                    existingAddedAdditionalSalaryData.unitCount +=
                      Number(unitCount);
                    existingAddedAdditionalSalaryData.definedDates.push(date);
                    existingAddedAdditionalSalaryData.dayUnitCount[date] =
                      Number(unitCount);
                  } else {
                    resources[resource.user].additionalSalaries[salaryType] = {
                      unitCount: Number(unitCount),
                      unitPrice: effectiveUnitPrice,
                      definedDates: [date],
                      salaryType,
                      dayUnitCount: {
                        [date]: Number(unitCount),
                      },
                    };
                  }
                  if (
                    !resources[resource.user].additionalSalariesByProjectId[
                      projectId
                    ]
                  ) {
                    resources[resource.user].additionalSalariesByProjectId[
                      projectId
                    ] = {};
                  }

                  const existingAdditionalSalaryByProject =
                    resources[resource.user].additionalSalariesByProjectId[
                      projectId
                    ][salaryType];

                  if (existingAdditionalSalaryByProject) {
                    existingAdditionalSalaryByProject.unitCount +=
                      Number(unitCount);
                    existingAdditionalSalaryByProject.definedDates.push(date);
                    existingAdditionalSalaryByProject.dayUnitCount[date] =
                      Number(unitCount);
                  } else {
                    resources[resource.user].additionalSalariesByProjectId[
                      projectId
                    ][salaryType] = {
                      projectId,
                      unitCount: Number(unitCount),
                      unitPrice: effectiveUnitPrice,
                      definedDates: [date],
                      salaryType,
                      dayUnitCount: {
                        [date]: Number(unitCount),
                      },
                      jydacomAssignmentId: record.assignment?.integrationId,
                      talosoftaProjectId: recordProject.talosoftaProjectId,
                    };
                  }
                });
              }

              if (allowanceRecords && allowanceRecords.length > 0) {
                allowanceRecords.forEach((allowanceRecord) => {
                  const existingAddedAdditionalSalaryData =
                    resources[resource.user].additionalSalaries[
                      allowanceRecord.type
                    ];
                  const momentDate = moment(record.croppedClockInAt);

                  const date = momentDate.format('D.M.');

                  const fullDate = momentDate.format('DD.MM.YYYY');

                  const allowanceRecordHours = round(
                    dayjs(allowanceRecord.clockOutAt).diff(
                      dayjs(allowanceRecord.clockInAt),
                      'hour',
                      true
                    ),
                    2
                  );

                  resources[resource.user].seperateAdditionalSalaries.push({
                    unitCount: allowanceRecordHours,
                    date: fullDate,
                    ISOStringDate: momentDate.toISOString(),
                    salaryType: allowanceRecord.type,
                    projectId: allowanceRecord.project,
                    talosoftaProjectId: recordProject.talosoftaProjectId,
                  });

                  if (existingAddedAdditionalSalaryData) {
                    existingAddedAdditionalSalaryData.unitCount +=
                      Number(allowanceRecordHours);
                    existingAddedAdditionalSalaryData.definedDates.push(date);
                    existingAddedAdditionalSalaryData.dayUnitCount[date] =
                      (existingAddedAdditionalSalaryData.dayUnitCount[date] ||
                        0) + Number(allowanceRecordHours);
                  } else {
                    resources[resource.user].additionalSalaries[
                      allowanceRecord.type
                    ] = {
                      unitCount: Number(allowanceRecordHours),
                      definedDates: [date],
                      salaryType: allowanceRecord.type,
                      dayUnitCount: {
                        [date]: Number(allowanceRecordHours),
                      },
                    };
                  }
                  if (
                    !resources[resource.user].additionalSalariesByProjectId[
                      allowanceRecord.project
                    ]
                  ) {
                    resources[resource.user].additionalSalariesByProjectId[
                      allowanceRecord.project
                    ] = {};
                  }

                  const existingAdditionalSalaryByProject =
                    resources[resource.user].additionalSalariesByProjectId[
                      allowanceRecord.project
                    ][allowanceRecord.type];

                  if (existingAdditionalSalaryByProject) {
                    existingAdditionalSalaryByProject.unitCount +=
                      Number(allowanceRecordHours);
                    existingAdditionalSalaryByProject.definedDates.push(date);
                    existingAdditionalSalaryByProject.dayUnitCount[date] =
                      Number(allowanceRecordHours);
                  } else {
                    resources[resource.user].additionalSalariesByProjectId[
                      allowanceRecord.project
                    ][allowanceRecord.type] = {
                      projectId: allowanceRecord.project,
                      unitCount: Number(allowanceRecordHours),
                      definedDates: [date],
                      salaryType: allowanceRecord.type,
                      dayUnitCount: {
                        [date]: Number(allowanceRecordHours),
                      },
                      jydacomAssignmentId: record.assignment?.integrationId,
                      talosoftaProjectId: recordProject.talosoftaProjectId,
                    };
                  }
                });
              }

              if (
                !resources[resource.user].workedProjects.find(
                  (workedProject) =>
                    workedProject.projectId === recordProject.id
                )
              ) {
                resources[resource.user].workedProjects.push({
                  projectName: recordProject.name,
                  projectId: recordProject.id,
                  talosoftaProjectId: recordProject.talosoftaProjectId,
                });
              }

              const recordStartTime = moment(ci);
              const recordEndTime = moment(co);

              const timeframeStart = moment(i).set({
                hour: 11,
                minute: 0,
                second: 0,
              });
              const timeframeEnd = moment(i).set({
                hour: 11,
                minute: 30,
                second: 0,
              });

              const overlapsMealBreakTimeframe =
                recordStartTime.isBefore(timeframeEnd) &&
                recordEndTime.isAfter(timeframeStart);

              if (!resources[resource.user].dailyProjectsAtMealBreak[day]) {
                resources[resource.user].dailyProjectsAtMealBreak[day] = [];
              }

              const dailyMealProjects =
                resources[resource.user].dailyProjectsAtMealBreak[day];

              if (overlapsMealBreakTimeframe) {
                const projectAlreadyCounted = dailyMealProjects.includes(
                  recordProject.id
                );
                if (!projectAlreadyCounted) {
                  dailyMealProjects.push(recordProject.id);
                }
              }

              if (thisDayHoursExceed6Hours) {
                const isPreviouslyMadeAutomaticReductionsForDay =
                  days[day][resource.user].reductions &&
                  days[day][resource.user].reductions[
                    'AUTOMATIC_MEAL_BREAK_REDUCTION'
                  ] > 0;

                if (dailyMealProjects.length > 0) {
                  // Apply reduction to projects that were active during meal break
                  dailyMealProjects.forEach((projectId) => {
                    const reductionPerProject =
                      automaticMealBreakReductionLengthHours /
                      dailyMealProjects.length;

                    days[day][resource.user].projectHours[projectId].reductions[
                      'AUTOMATIC_MEAL_BREAK_REDUCTION'
                    ] = reductionPerProject;

                    const existingCountedReductionsForProject =
                      resources[resource.user].totals.reductionsByProject[
                        projectId
                      ];
                    if (!existingCountedReductionsForProject) {
                      resources[resource.user].totals.reductionsByProject[
                        projectId
                      ] = {
                        BREAK_MEAL: 0,
                        BREAK_COFFEE: 0,
                        BREAK: 0,
                        AUTOMATIC_MEAL_BREAK_REDUCTION: 0,
                      };
                    }

                    resources[resource.user].totals.reductionsByProject[
                      projectId
                    ]['AUTOMATIC_MEAL_BREAK_REDUCTION'] += reductionPerProject;
                  });
                } else {
                  // if no project previously set to meal time, then just reduce from current record / project
                  if (!isPreviouslyMadeAutomaticReductionsForDay) {
                    days[day][resource.user].projectHours[
                      recordProject.id
                    ].reductions['AUTOMATIC_MEAL_BREAK_REDUCTION'] =
                      automaticMealBreakReductionLengthHours;

                    const existingCountedReductionsForProject =
                      resources[resource.user].totals.reductionsByProject[
                        recordProject.id
                      ];
                    if (!existingCountedReductionsForProject) {
                      resources[resource.user].totals.reductionsByProject[
                        recordProject.id
                      ] = {
                        BREAK_MEAL: 0,
                        BREAK_COFFEE: 0,
                        BREAK: 0,
                        AUTOMATIC_MEAL_BREAK_REDUCTION: 0,
                      };
                    }

                    resources[resource.user].totals.reductionsByProject[
                      recordProject.id
                    ]['AUTOMATIC_MEAL_BREAK_REDUCTION'] +=
                      automaticMealBreakReductionLengthHours;
                  }
                }
              }
            } else if (record.type.includes('ABSENCE')) {
              // handle global resource additional salary data

              const momentDate = moment(record.croppedClockInAt);

              const date = momentDate.format('D.M.');
              const fullDate = momentDate.format('DD.MM.YYYY');

              const salaryType = record.subType
                ? record.subType
                : record.type === RECORD_TYPE.ABSENCE_SICK_LEAVE
                  ? 'SICK_LEAVE'
                  : 'HOLIDAY_PAY';

              const existingAddedAdditionalSalaryData =
                resources[resource.user].additionalSalaries[salaryType];

              const baseType = 'ABSENCE';

              resources[resource.user].seperateAdditionalSalaries.push({
                unitCount: 1,
                date: fullDate,
                ISOStringDate: momentDate.toISOString(),
                salaryType,
                baseType,
                talosoftaProjectId: recordProject.talosoftaProjectId,
              });

              if (existingAddedAdditionalSalaryData) {
                existingAddedAdditionalSalaryData.unitCount += 1;
                existingAddedAdditionalSalaryData.definedDates.push(date);
              } else {
                resources[resource.user].additionalSalaries[salaryType] = {
                  unitCount: 1,
                  definedDates: [date],
                  salaryType,
                  baseType,
                };
              }

              // handle project specific additional salary data adding
              if (
                !resources[resource.user].additionalSalariesByProjectId[
                  recordProject.id
                ]
              ) {
                resources[resource.user].additionalSalariesByProjectId[
                  recordProject.id
                ] = {};
              }

              const existingAdditionalSalaryByProject =
                resources[resource.user].additionalSalariesByProjectId[
                  recordProject.id
                ][salaryType];

              if (existingAdditionalSalaryByProject) {
                existingAdditionalSalaryByProject.unitCount += 1;
                existingAdditionalSalaryByProject.definedDates.push(date);
              } else {
                resources[resource.user].additionalSalariesByProjectId[
                  recordProject.id
                ][salaryType] = {
                  unitCount: 1,
                  definedDates: [date],
                  salaryType,
                  baseType,
                  talosoftaProjectId: recordProject.talosoftaProjectId,
                };
              }
              if (
                !resources[resource.user].workedProjects.find(
                  (workedProject) =>
                    workedProject.projectId === recordProject.id
                )
              ) {
                resources[resource.user].workedProjects.push({
                  projectName: recordProject.name,
                  projectId: recordProject.id,
                  talosoftaProjectId: recordProject.talosoftaProjectId,
                });
              }
            }
            resources[resource.user].totalHours += hours;
            resources[resource.user].totals[record.type] += hours;
          }
          i.add(1, 'days');
        }
      }
    });

    // Post-process to handle automatic meal break reductions for days with no meal break overlaps
    Object.keys(days).forEach((day) => {
      Object.keys(days[day]).forEach((userId) => {
        const dayData = days[day][userId];
        const userResources = resources[userId];

        if (
          dayData.hours > 6 &&
          dayData.reductions['AUTOMATIC_MEAL_BREAK_REDUCTION'] ===
            automaticMealBreakReductionLengthHours
        ) {
          const dailyMealProjects =
            userResources.dailyProjectsAtMealBreak[day] || [];

          if (dailyMealProjects.length === 0) {
            // Find the first project worked on this day to apply the reduction
            const projectIds = Object.keys(dayData.projectHours);
            if (projectIds.length > 0) {
              const firstProjectId = projectIds[0];

              dayData.projectHours[firstProjectId].reductions[
                'AUTOMATIC_MEAL_BREAK_REDUCTION'
              ] = automaticMealBreakReductionLengthHours;

              if (!userResources.totals.reductionsByProject[firstProjectId]) {
                userResources.totals.reductionsByProject[firstProjectId] = {
                  BREAK_MEAL: 0,
                  BREAK_COFFEE: 0,
                  BREAK: 0,
                  AUTOMATIC_MEAL_BREAK_REDUCTION: 0,
                };
              }

              userResources.totals.reductionsByProject[firstProjectId][
                'AUTOMATIC_MEAL_BREAK_REDUCTION'
              ] += automaticMealBreakReductionLengthHours;
            }
          }
        }
      });
    });

    reportData['resources'] = resources;
    return reportData;
  };

  const runProcesses = () => {
    settings.processes.forEach((process) => {
      reportData = processes[process.type](reportData, process);
    });
    return reportData;
  };

  reportData = trimRecordsTime();
  reportData = initDays();
  reportData = baseHours();
  reportData = runProcesses();

  return reportData;
};
